/**
 * WebOs - Module d'authentification
 *
 * Gère le hachage et la vérification des mots de passe.
 * Utilise la Web Crypto API (SHA-256 + salt).
 */

import { createLogger } from '../utils/debug.js';

const log = createLogger('Auth');

/**
 * Génère un salt aléatoire
 */
function generateSalt(length = 16) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Hache un mot de passe avec SHA-256 + salt
 */
async function hashPassword(password, salt = null) {
  if (!salt) {
    salt = generateSalt();
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(salt + password);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return {
    hash: `sha256:${salt}:${hashHex}`,
    salt
  };
}

/**
 * Vérifie un mot de passe contre un hash stocké
 */
async function verifyPassword(password, storedHash) {
  if (!storedHash || !storedHash.startsWith('sha256:')) {
    log.error('Invalid hash format');
    return false;
  }

  const parts = storedHash.split(':');
  if (parts.length !== 3) {
    log.error('Invalid hash format (expected 3 parts)');
    return false;
  }

  const [algorithm, salt, expectedHash] = parts;

  const { hash } = await hashPassword(password, salt);
  const computedHash = hash.split(':')[2];

  return computedHash === expectedHash;
}

/**
 * Classe Auth - Singleton pour l'authentification
 */
class Auth {
  constructor() {
    this.fs = null;
    this.currentUser = null;
    this.sudoSession = null;  // { user, expiresAt }
  }

  /**
   * Configure le module avec le filesystem
   */
  configure(fs) {
    this.fs = fs;
  }

  /**
   * Vérifie si c'est le premier boot (pas de /etc/passwd)
   */
  async isFirstBoot() {
    try {
      return !(await this.fs.exists('/etc/passwd'));
    } catch (err) {
      log.error('Error checking first boot:', err);
      return true;
    }
  }

  /**
   * Crée le premier utilisateur (admin)
   */
  async createFirstUser(username, password, displayName = null) {
    log.info('Creating first user:', username);

    const { hash } = await hashPassword(password);
    const now = Date.now();

    // Créer /etc/passwd
    const passwd = {
      [username]: {
        uid: 0,
        gid: 0,
        name: displayName || username,
        home: `/home/${username}`,
        shell: '/bin/websh',
        created: now
      }
    };

    // Créer /etc/shadow
    const shadow = {
      [username]: {
        hash,
        lastChanged: now
      }
    };

    // Créer /etc/sudoers (premier utilisateur est admin)
    const sudoers = {
      [username]: true
    };

    // Écrire les fichiers
    await this.fs.writeFile('/etc/passwd', JSON.stringify(passwd, null, 2));
    await this.fs.writeFile('/etc/shadow', JSON.stringify(shadow, null, 2));
    await this.fs.writeFile('/etc/sudoers', JSON.stringify(sudoers, null, 2));

    // Créer le répertoire home
    await this.createHomeDirectory(username);

    // Logger l'événement
    await this.logAuth('user_created', username, true, 'First user (admin)');

    log.info('First user created successfully');
    return true;
  }

  /**
   * Crée le répertoire home d'un utilisateur
   */
  async createHomeDirectory(username) {
    const homePath = `/home/${username}`;

    await this.fs.mkdir(homePath, { recursive: true });
    await this.fs.mkdir(`${homePath}/.config`, { recursive: true });
    await this.fs.mkdir(`${homePath}/.local/apps`, { recursive: true });
    await this.fs.mkdir(`${homePath}/Documents`, { recursive: true });

    // Créer un fichier .profile basique
    const profile = `# WebOs Profile for ${username}
export PATH="~/.local/apps:$PATH"
`;
    await this.fs.writeFile(`${homePath}/.profile`, profile);

    // Créer le fichier about.txt avec les informations sur WebOs
    const aboutContent = `================================================================================
                              BIENVENUE SUR WEBOS
================================================================================

Je m'appelle Gabriel Granier, je suis en spé NSI au lycée Pasteur en Terminale
au moment où j'écris ça (2024-2025), et voici WebOs.

WebOs est un système d'exploitation complet qui tourne... dans un navigateur web.
Oui, vous avez bien lu. Un OS. Dans un navigateur. Avec un vrai kernel, un vrai
filesystem, un vrai shell, de vrais processus, et une vraie gestion utilisateur.

================================================================================
                           POURQUOI C'EST IMPRESSIONNANT ?
================================================================================

- KERNEL COMPLET : Un vrai kernel qui gère les processus, la mémoire, et l'IPC
  (Inter-Process Communication) entre les applications via MessagePorts.

- FILESYSTEM PERSISTANT : Un système de fichiers complet stocké dans IndexedDB,
  avec une vraie arborescence Unix (/home, /etc, /usr, /var...). Vos fichiers
  persistent même après fermeture du navigateur.

- PROCESSUS ISOLÉS : Chaque application tourne dans son propre Web Worker,
  complètement isolée des autres. Comme un vrai OS ! Un crash d'app ne fait
  pas planter le système.

- SHELL BASH-LIKE : Un terminal avec 40+ commandes built-in, support des pipes
  (|), des redirections (>, >>), de l'historique, de l'autocomplétion...

- GESTIONNAIRE DE PAQUETS : Installez des apps avec "web install <package>",
  comme apt ou npm, mais pour WebOs. Gestion automatique des dépendances.

- APPLICATIONS GUI : Un vrai window manager avec fenêtres déplaçables, un
  bureau (deskweb), gestionnaire de fichiers, éditeur de texte, calculatrice...

- MULTI-UTILISATEURS : Authentification avec mots de passe hashés (SHA-256),
  sudo, permissions, et sessions utilisateur séparées.

================================================================================
                           LES POSSIBILITÉS - C'EST LÀ QUE ÇA DEVIENT FOU
================================================================================

WEBASSEMBLY - LE GAME CHANGER :
  WebOs peut faire tourner n'importe quelle application WebAssembly ! Ça veut
  dire que des langages comme Python, Rust, C, C++, Go... peuvent être compilés
  en WASM et tourner nativement dans WebOs. L'exemple parfait : Python tourne
  via Pyodide, un port complet de CPython en WebAssembly. Vous avez un vrai
  interpréteur Python avec numpy, pandas, et des centaines de packages !

SDK COMPLET POUR DÉVELOPPEURS :
  Le SDK WebOs permet de créer facilement des applications :
  - Applications TUI (Terminal UI) : apps en mode texte dans le terminal
  - Applications GUI : fenêtres graphiques avec composants (boutons, panels...)
  - Daemons : services en arrière-plan (ex: système de notifications)
  - État réactif intégré : changez une variable, l'UI se met à jour

EXTENSIBILITÉ INFINIE :
  - Ajoutez vos propres commandes shell
  - Créez des packages installables via "web install"
  - Les apps peuvent communiquer entre elles via l'IPC du kernel
  - Service Worker pour intercepter les requêtes filesystem

ISOLATION ET SÉCURITÉ :
  - Chaque processus est sandboxé dans son Worker
  - Système de permissions (en développement)
  - Gatekeeper pour contrôler l'accès aux ressources

CE QUE VOUS POUVEZ FAIRE CONCRÈTEMENT :
  - Coder en Python directement dans le navigateur
  - Créer, éditer, organiser vos fichiers comme sur un vrai OS
  - Développer et tester des applications WebOs
  - Utiliser un environnement de bureau complet (deskweb)
  - Gérer plusieurs utilisateurs avec leurs propres fichiers
  - Et techniquement... faire tourner n'importe quoi compilable en WASM !

================================================================================
                           COMMANDES ESSENTIELLES
================================================================================

  help                    Affiche l'aide du shell
  man <commande>          Manuel d'une commande
  web search <query>      Cherche des paquets à installer
  web install <pkg>       Installe un paquet
  web install --all       Installe TOUS les paquets disponibles
  hardreset               Réinitialise WebOs à zéro

================================================================================
                           ARCHITECTURE TECHNIQUE
================================================================================

  Frontend    : Vanilla JS + xterm.js (terminal)
  Stockage    : IndexedDB (filesystem persistant)
  Processus   : Web Workers (isolation)
  IPC         : MessagePorts (communication inter-processus)
  Build       : Vite
  GUI         : Window Manager custom + iframes sandboxées

================================================================================

Un projet fait à 17h59.

- Gabriel Granier

================================================================================
`;
    await this.fs.writeFile(`${homePath}/about.txt`, aboutContent);

    log.debug('Home directory created:', homePath);
  }

  /**
   * Authentifie un utilisateur
   */
  async login(username, password) {
    try {
      // Lire /etc/passwd
      const passwdContent = await this.fs.readFile('/etc/passwd');
      const passwd = JSON.parse(passwdContent);

      if (!passwd[username]) {
        await this.logAuth('login', username, false, 'User not found');
        return { success: false, error: 'Utilisateur inconnu' };
      }

      // Lire /etc/shadow
      const shadowContent = await this.fs.readFile('/etc/shadow');
      const shadow = JSON.parse(shadowContent);

      if (!shadow[username]) {
        await this.logAuth('login', username, false, 'No password entry');
        return { success: false, error: 'Erreur de configuration' };
      }

      // Vérifier le mot de passe
      const valid = await verifyPassword(password, shadow[username].hash);

      if (!valid) {
        await this.logAuth('login', username, false, 'Invalid password');
        return { success: false, error: 'Mot de passe incorrect' };
      }

      // Connexion réussie
      this.currentUser = {
        username,
        ...passwd[username]
      };

      await this.logAuth('login', username, true);
      log.info('User logged in:', username);

      return { success: true, user: this.currentUser };

    } catch (err) {
      log.error('Login error:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Déconnecte l'utilisateur courant
   */
  logout() {
    if (this.currentUser) {
      log.info('User logged out:', this.currentUser.username);
      this.currentUser = null;
      this.sudoSession = null;
    }
  }

  /**
   * Retourne l'utilisateur courant
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Vérifie si l'utilisateur courant a les droits sudo
   */
  async canSudo() {
    if (!this.currentUser) return false;

    try {
      const sudoersContent = await this.fs.readFile('/etc/sudoers');
      const sudoers = JSON.parse(sudoersContent);
      return sudoers[this.currentUser.username] === true;
    } catch (err) {
      log.error('Error checking sudo:', err);
      return false;
    }
  }

  /**
   * Vérifie le mot de passe pour sudo
   */
  async verifySudo(password) {
    if (!this.currentUser) return false;

    // Vérifier si session sudo encore valide (5 minutes)
    if (this.sudoSession &&
        this.sudoSession.user === this.currentUser.username &&
        Date.now() < this.sudoSession.expiresAt) {
      return true;
    }

    try {
      const shadowContent = await this.fs.readFile('/etc/shadow');
      const shadow = JSON.parse(shadowContent);

      const valid = await verifyPassword(password, shadow[this.currentUser.username].hash);

      if (valid) {
        // Créer une session sudo de 5 minutes
        this.sudoSession = {
          user: this.currentUser.username,
          expiresAt: Date.now() + (5 * 60 * 1000)
        };
        await this.logAuth('sudo', this.currentUser.username, true);
      } else {
        await this.logAuth('sudo', this.currentUser.username, false, 'Invalid password');
      }

      return valid;
    } catch (err) {
      log.error('Sudo verification error:', err);
      return false;
    }
  }

  /**
   * Change le mot de passe d'un utilisateur
   */
  async changePassword(username, oldPassword, newPassword) {
    try {
      // Vérifier l'ancien mot de passe
      const shadowContent = await this.fs.readFile('/etc/shadow');
      const shadow = JSON.parse(shadowContent);

      if (!shadow[username]) {
        return { success: false, error: 'Utilisateur inconnu' };
      }

      const valid = await verifyPassword(oldPassword, shadow[username].hash);
      if (!valid) {
        await this.logAuth('passwd', username, false, 'Invalid old password');
        return { success: false, error: 'Ancien mot de passe incorrect' };
      }

      // Hasher le nouveau mot de passe
      const { hash } = await hashPassword(newPassword);

      // Mettre à jour shadow
      shadow[username] = {
        hash,
        lastChanged: Date.now()
      };

      await this.fs.writeFile('/etc/shadow', JSON.stringify(shadow, null, 2));
      await this.logAuth('passwd', username, true);

      log.info('Password changed for:', username);
      return { success: true };

    } catch (err) {
      log.error('Change password error:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Liste tous les utilisateurs
   */
  async listUsers() {
    try {
      const passwdContent = await this.fs.readFile('/etc/passwd');
      const passwd = JSON.parse(passwdContent);
      return Object.keys(passwd).map(username => ({
        username,
        ...passwd[username]
      }));
    } catch (err) {
      log.error('Error listing users:', err);
      return [];
    }
  }

  /**
   * Crée un nouvel utilisateur (nécessite sudo)
   */
  async createUser(username, password, options = {}) {
    try {
      // Vérifier que l'utilisateur n'existe pas déjà
      const passwdContent = await this.fs.readFile('/etc/passwd');
      const passwd = JSON.parse(passwdContent);

      if (passwd[username]) {
        return { success: false, error: 'Utilisateur existe déjà' };
      }

      // Trouver le prochain UID
      const uids = Object.values(passwd).map(u => u.uid);
      const nextUid = Math.max(...uids, 999) + 1;

      const { hash } = await hashPassword(password);
      const now = Date.now();

      // Ajouter à passwd
      passwd[username] = {
        uid: nextUid,
        gid: nextUid,
        name: options.displayName || username,
        home: `/home/${username}`,
        shell: '/bin/websh',
        created: now
      };

      // Ajouter à shadow
      const shadowContent = await this.fs.readFile('/etc/shadow');
      const shadow = JSON.parse(shadowContent);
      shadow[username] = {
        hash,
        lastChanged: now
      };

      // Ajouter à sudoers si demandé
      const sudoersContent = await this.fs.readFile('/etc/sudoers');
      const sudoers = JSON.parse(sudoersContent);
      sudoers[username] = options.sudo === true;

      // Écrire les fichiers
      await this.fs.writeFile('/etc/passwd', JSON.stringify(passwd, null, 2));
      await this.fs.writeFile('/etc/shadow', JSON.stringify(shadow, null, 2));
      await this.fs.writeFile('/etc/sudoers', JSON.stringify(sudoers, null, 2));

      // Créer le home
      await this.createHomeDirectory(username);

      await this.logAuth('user_created', username, true,
        `Created by ${this.currentUser?.username || 'system'}`);

      log.info('User created:', username);
      return { success: true };

    } catch (err) {
      log.error('Create user error:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Supprime un utilisateur (nécessite sudo)
   */
  async deleteUser(username, removeHome = false) {
    try {
      // Ne pas supprimer l'utilisateur courant
      if (this.currentUser?.username === username) {
        return { success: false, error: 'Impossible de supprimer l\'utilisateur courant' };
      }

      const passwdContent = await this.fs.readFile('/etc/passwd');
      const passwd = JSON.parse(passwdContent);

      if (!passwd[username]) {
        return { success: false, error: 'Utilisateur inconnu' };
      }

      // Ne pas supprimer le dernier admin
      const sudoersContent = await this.fs.readFile('/etc/sudoers');
      const sudoers = JSON.parse(sudoersContent);

      const adminCount = Object.values(sudoers).filter(v => v === true).length;
      if (sudoers[username] && adminCount <= 1) {
        return { success: false, error: 'Impossible de supprimer le dernier administrateur' };
      }

      const homePath = passwd[username].home;

      // Supprimer de passwd
      delete passwd[username];

      // Supprimer de shadow
      const shadowContent = await this.fs.readFile('/etc/shadow');
      const shadow = JSON.parse(shadowContent);
      delete shadow[username];

      // Supprimer de sudoers
      delete sudoers[username];

      // Écrire les fichiers
      await this.fs.writeFile('/etc/passwd', JSON.stringify(passwd, null, 2));
      await this.fs.writeFile('/etc/shadow', JSON.stringify(shadow, null, 2));
      await this.fs.writeFile('/etc/sudoers', JSON.stringify(sudoers, null, 2));

      // Supprimer le home si demandé
      if (removeHome) {
        await this.fs.delete(homePath, { recursive: true });
      }

      await this.logAuth('user_deleted', username, true,
        `Deleted by ${this.currentUser?.username || 'system'}`);

      log.info('User deleted:', username);
      return { success: true };

    } catch (err) {
      log.error('Delete user error:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Modifie les droits sudo d'un utilisateur
   */
  async setSudo(username, canSudo) {
    try {
      const sudoersContent = await this.fs.readFile('/etc/sudoers');
      const sudoers = JSON.parse(sudoersContent);

      // Vérifier qu'on ne retire pas le dernier admin
      if (!canSudo && sudoers[username]) {
        const adminCount = Object.values(sudoers).filter(v => v === true).length;
        if (adminCount <= 1) {
          return { success: false, error: 'Impossible de retirer les droits du dernier administrateur' };
        }
      }

      sudoers[username] = canSudo;
      await this.fs.writeFile('/etc/sudoers', JSON.stringify(sudoers, null, 2));

      await this.logAuth('sudo_change', username, true,
        `sudo=${canSudo} by ${this.currentUser?.username}`);

      log.info(`Sudo rights for ${username}: ${canSudo}`);
      return { success: true };

    } catch (err) {
      log.error('Set sudo error:', err);
      return { success: false, error: err.message };
    }
  }

  /**
   * Journalise un événement d'authentification
   */
  async logAuth(action, username, success, details = '') {
    try {
      const logPath = '/var/log/auth.log';
      const timestamp = new Date().toISOString();
      const status = success ? 'SUCCESS' : 'FAILED';
      const entry = `${timestamp} ${action} ${username} ${status} ${details}\n`;

      // Lire le fichier existant ou créer
      let existing = '';
      if (await this.fs.exists(logPath)) {
        existing = await this.fs.readFile(logPath);
      }

      await this.fs.writeFile(logPath, existing + entry);
    } catch (err) {
      // Ne pas bloquer si le log échoue
      log.warn('Failed to write auth log:', err.message);
    }
  }
}

// Singleton
let authInstance = null;

export function getAuth() {
  if (!authInstance) {
    authInstance = new Auth();
  }
  return authInstance;
}

export { hashPassword, verifyPassword };
